import React, { useEffect, useState } from 'react';
import { Save, RefreshCw, Clock, Video } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { getCalendarSettings, kalenderEinstellungenMerken, updateCalendarSettings } from '../../../lib/settings';
import { api } from '../../../lib/api';
import { toast } from 'sonner';
import { usePermission } from '../../../components/rbac/use-permission';
import { P } from '../../../lib/rbac/permissions.generated';

const CalendarSettings: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [startHour, setStartHour] = useState<number>(8);
  const [endHour, setEndHour] = useState<number>(20);
  const [timeInterval, setTimeInterval] = useState<number>(15);
  const [timezone, setTimezone] = useState('Europe/Berlin');
  const [savingTimezone, setSavingTimezone] = useState(false);
  // Ohne Bearbeiten-Recht nur ansehen (ADM-19.3) — das Backend prüft dieselben Rechte.
  const darfZeitbereich = usePermission(P.admin.system_und_schnittstellen.terminintervalle_verwalten);
  const darfZeitzone = usePermission(P.admin.system_und_schnittstellen.zeitzonen_verwalten);
  // Online-Kurs: Standard-Meeting-Link (TERM-8.3)
  const [onlineCourseEnabled, setOnlineCourseEnabled] = useState(false);
  const [defaultMeetingUrl, setDefaultMeetingUrl] = useState('');

  // Bekannte Zeitzonen für Deutschland/Europa
  const commonTimezones = [
    { value: 'Europe/Berlin', label: 'Europa/Berlin (MEZ/MESZ)' },
    { value: 'Europe/London', label: 'Europa/London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Europa/Paris (MEZ/MESZ)' },
    { value: 'Europe/Vienna', label: 'Europa/Wien (MEZ/MESZ)' },
    { value: 'Europe/Zurich', label: 'Europa/Zürich (MEZ/MESZ)' },
    { value: 'Europe/Rome', label: 'Europa/Rom (MEZ/MESZ)' },
    { value: 'Europe/Madrid', label: 'Europa/Madrid (MEZ/MESZ)' },
    { value: 'Europe/Amsterdam', label: 'Europa/Amsterdam (MEZ/MESZ)' },
    { value: 'UTC', label: 'UTC' },
  ];

  useEffect(() => {
    (async () => {
      try {
        const s = await getCalendarSettings();
        setStartHour(s.startHour);
        setEndHour(s.endHour);
        setTimeInterval(s.timeInterval);
        setOnlineCourseEnabled(!!s.onlineCourseEnabled);
        setDefaultMeetingUrl(s.defaultMeetingUrl || '');
        const tz = await api.get<{ timezone: string }>('/api/settings/timezone');
        setTimezone(tz.timezone || 'Europe/Berlin');
      } catch {
        // Keep fallback defaults when settings can't be loaded.
      }
    })();
  }, []);

  const onSubmit = async () => {
    setIsLoading(true);
    try {
      if (startHour >= endHour) {
        toast.error('Die Endstunde muss nach der Startstunde liegen');
        return;
      }
      const url = defaultMeetingUrl.trim();
      if (url && !/^https?:\/\//i.test(url)) {
        toast.error('Der Meeting-Link muss mit http:// oder https:// beginnen');
        return;
      }
      const saved = await updateCalendarSettings({
        startHour,
        endHour,
        timeInterval,
        onlineCourseEnabled,
        defaultMeetingUrl: url,
      });
      setStartHour(saved.startHour);
      setEndHour(saved.endHour);
      setTimeInterval(saved.timeInterval);
      // Terminübersicht und Raumplan lesen die Werte synchron (ADM-19.1).
      kalenderEinstellungenMerken(saved);
      toast.success('Kalendereinstellungen gespeichert');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  const onReset = () => {
    setStartHour(8);
    setEndHour(20);
    setTimeInterval(15);
  };

  const saveTimezone = async () => {
    setSavingTimezone(true);
    try {
      await api.put('/api/settings/timezone', { timezone });
      kalenderEinstellungenMerken({ timezone });
      toast.success('Zeitzone gespeichert');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSavingTimezone(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Kalender & Zeiten</h1>
        <p className="text-muted-foreground">Start-/Endzeit und Intervall für Termin- und Raumpläne</p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center"><Clock className="mr-2 h-5 w-5" />Zeitbereich</CardTitle>
          <CardDescription>Gilt für Terminübersicht und Raumplan</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="start-hour" className="text-sm font-medium">Startstunde (0-23)</label>
              <Input id="start-hour" disabled={!darfZeitbereich} type="number" min={0} max={23} value={startHour} onChange={e => setStartHour(Math.max(0, Math.min(23, parseInt(e.target.value || '0'))))} />
            </div>
            <div>
              <label htmlFor="end-hour" className="text-sm font-medium">Endstunde (0-23)</label>
              <Input id="end-hour" disabled={!darfZeitbereich} type="number" min={0} max={23} value={endHour} onChange={e => setEndHour(Math.max(0, Math.min(23, parseInt(e.target.value || '0'))))} />
            </div>
            <div>
              <label htmlFor="time-interval" className="text-sm font-medium">Intervall (Minuten)</label>
              <Input id="time-interval" disabled={!darfZeitbereich} type="number" min={1} max={120} value={timeInterval} onChange={e => setTimeInterval(Math.max(1, Math.min(120, parseInt(e.target.value || '1'))))} />
            </div>
          </div>
        </CardContent>
        <CardContent className="space-y-4 border-t pt-6">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Video className="h-4 w-4" />
            Online-Kurse
          </div>
          <div className="flex items-center justify-between gap-4">
            <label htmlFor="online-course-enabled" className="text-sm text-muted-foreground">
              Standard-Meeting-Link in den Kursdetails anbieten
            </label>
            <Switch id="online-course-enabled" disabled={!darfZeitbereich} checked={onlineCourseEnabled} onCheckedChange={setOnlineCourseEnabled} />
          </div>
          <div>
            <label htmlFor="default-meeting-url" className="text-sm font-medium">Standard-Meeting-Link</label>
            <Input
              id="default-meeting-url"
              disabled={!darfZeitbereich}
              type="url"
              placeholder="https://…"
              value={defaultMeetingUrl}
              onChange={(e) => setDefaultMeetingUrl(e.target.value)}
            />
          </div>
        </CardContent>
        <CardFooter className="flex justify-end gap-2">
          <Button variant="outline" onClick={onReset} disabled={isLoading || !darfZeitbereich}><RefreshCw className="mr-2 h-4 w-4" />Zurücksetzen</Button>
          <Button onClick={onSubmit} disabled={isLoading || !darfZeitbereich}>{isLoading ? <><RefreshCw className="mr-2 h-4 w-4 animate-spin" />Speichern...</> : <><Save className="mr-2 h-4 w-4" />Speichern</>}</Button>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center"><Clock className="mr-2 h-5 w-5" />Zeitzone</CardTitle>
          <CardDescription>Wird für alle terminbezogenen Dinge verwendet (Kurse, Probestunden, Aufgaben, Terminübersicht)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="text-sm text-muted-foreground mb-4">Wähle die Zeitzone, die für das gesamte System gilt.</div>
            </div>
            <div className="flex items-center gap-4">
              <Select value={timezone} onValueChange={setTimezone} disabled={!darfZeitzone}>
                <SelectTrigger className="w-[280px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {commonTimezones.map(tz => (
                    <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={saveTimezone} disabled={savingTimezone || !darfZeitzone}>
                {savingTimezone ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Speichern...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Speichern
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CalendarSettings;


